import React, { useState } from 'react';
import { BehaviorSettings, DefaultPictureType, LinkWithLabel } from '../../../../../types';
import { generateClassNameByBehaviorSettings } from '../../../../../utils/behaviorSettings';
import Slider from '../../../../01_atoms/Slider';
import DefaultPicture from '../../../../01_atoms/DefaultPicture';
import Button, { ButtonType } from '../../../../01_atoms/Button';

import styles from './index.module.scss';

type Variant =
  | 'gifts_in_general'
  | 'santa_letter'
  | 'product_promo_turquoise_800'
  | 'product_promo_pink_1000'
  | 'product_promo_pink_800';

type SlideItem = {
  title: string;
  description?: string;
  image: DefaultPictureType;
  link?: LinkWithLabel;
  variant?: Variant;
};

type Props = {
  items: SlideItem[];
  behaviorSettings?: BehaviorSettings;
  uuid?: string;
};

type SlideProps = {
  item: SlideItem;
  index: number;
};

const BBHeroSliderItem = ({ item, index }: SlideProps) => {
  let ctaButtonType: ButtonType = 'primary';

  if (item.variant === 'santa_letter') {
    ctaButtonType = 'xmas';
  } else if (item.variant?.startsWith('product_promo_pink_')) {
    ctaButtonType = 'secondary';
  }

  return (
    <div
      className={`${styles['bb-hero-slider__slide']} ${styles['bb-hero-slider__slide--' + item.variant]}`}
    >
      <div className={styles['bb-hero-slider__slide_wrapper']}>
        <div className={styles['bb-hero-slider__overlay']}>
          <div className={styles['bb-hero-slider__text']}>
            {index === 0 ? (
              <h1 className={styles['bb-hero-slider__title']}>{item.title}</h1>
            ) : (
              <div className={styles['bb-hero-slider__title']}>{item.title}</div>
            )}
            {item.description && (
              <div className={styles['bb-hero-slider__description']}>{item.description}</div>
            )}
            {item.link && item.link.nextLink && (
              <Button
                tag="a"
                href={item.link.nextLink.url}
                type={ctaButtonType}
                size="small"
                withArrow={true}
              >
                {item.link.label}
              </Button>
            )}
          </div>
        </div>
        <div className={styles['bb-hero-slider__image']}>
          <DefaultPicture {...item.image} loading={index === 0 ? 'eager' : 'lazy'} />
        </div>
      </div>
    </div>
  );
};

const BBHeroSlider = ({ items, behaviorSettings, uuid }: Props) => {
  const [infinite, setInfinite] = useState(false);
  if (!items || items.length === 0) {
    return null;
  }

  const classes = [
    'bb',
    styles['bb-hero-slider'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  const sliderSettings = {
    dots: true,
    infinite: infinite,
    fade: true,
    speed: 500,
    autoplaySpeed: 5000,
    autoplay: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  } as const;

  return (
    <div className={classes.join(' ')} id={uuid} data-component={'bb-hero-slider'}>
      <div
        className={`${styles['bb-hero-slider__container-wrapper']} ${items.length === 1 ? styles['bb-hero-slider--single'] : null}`}
      >
        <div className={styles['bb-hero-slider__content']}>
          <div className={styles['bb-hero-slider__slider']}>
            <Slider {...sliderSettings} onInit={() => setTimeout(() => setInfinite(true), 1000)}>
              {items.map((item, index) => (
                <BBHeroSliderItem key={`slide-${index}`} item={item} index={index} />
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BBHeroSlider;
